import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

/**
 * HTML component that renders full HTML (doctype/head/body allowed)
 * into a Shadow DOM with script execution that uses a "document" wrapper
 * pointing to the shadowRoot. This prevents conflicts with the app's
 * global document while still allowing scripts to use document.getElementById, etc.
 */
const HTML = ({ htmlContent = "" }) => {
  const hostRef = useRef(null);
  const shadowRef = useRef(null);

  useEffect(() => {
    if (!hostRef.current) return;

    // Create or get shadow root
    let shadowRoot = shadowRef.current;
    if (!shadowRoot) {
      shadowRoot = hostRef.current.attachShadow({ mode: "open" });
      shadowRef.current = shadowRoot;
    }

    // Helper: Clear existing shadow content
    const clearShadow = () => {
      // remove children
      Array.from(shadowRoot.children).forEach((c) => c.remove());
    };
    clearShadow();

    // Parse incoming html string into a DocumentFragment using DOMParser
    const parser = new DOMParser();
    // If htmlContent includes <!doctype> or full html, parse as 'text/html'
    const doc = parser.parseFromString(htmlContent, "text/html");

    // --- Extract styles from <head> and append to shadowRoot ---
    const head = doc.head;
    if (head) {
      // Move <style> and <link rel="stylesheet"> into the shadow root
      const styleElements = Array.from(head.querySelectorAll("style, link[rel='stylesheet']"));

      styleElements.forEach((el) => {
        // If it's a <link>, create a new link inside shadow root
        if (el.tagName.toLowerCase() === "link") {
          const newLink = document.createElement("link");
          newLink.rel = "stylesheet";
          // copy href and other relevant attributes
          if (el.href) newLink.href = el.href;
          Array.from(el.attributes).forEach((attr) => {
            if (attr.name !== "rel" && attr.name !== "href") newLink.setAttribute(attr.name, attr.value);
          });
          shadowRoot.appendChild(newLink);
        } else {
          // style element - clone and append
          const cloned = document.createElement("style");
          cloned.textContent = el.textContent;
          shadowRoot.appendChild(cloned);
        }
      });
    }

    // --- Create a wrapper container inside shadowRoot to host body content ---
    const container = document.createElement("div");
    // optional: give an id/class to the wrapper for debugging
    container.setAttribute("data-bj-html-wrapper", "true");
    shadowRoot.appendChild(container);

    // Move body children into container
    const body = doc.body;
    if (body) {
      // Move nodes (clone to avoid issues)
      Array.from(body.childNodes).forEach((node) => {
        // Skip inline scripts for now; they will be handled later
        if (node.tagName && node.tagName.toLowerCase() === "script") {
          // skip: handle scripts separately
        } else {
          container.appendChild(node.cloneNode(true));
        }
      });
    } else {
      // If no body (maybe user passed fragment), parse and append whole input
      const frag = parser.parseFromString(htmlContent, "text/html");
      Array.from(frag.body.childNodes).forEach((n) => container.appendChild(n.cloneNode(true)));
    }

    // --- Helper: document wrapper that proxies DOM queries into the shadowRoot ---
    // This object provides the common APIs scripts expect: getElementById, querySelector, querySelectorAll, createElement, body
    const docWrapper = {
      getElementById: (id) => {
        if (!id) return null;
        // prefer searching inside container
        try {
          return container.querySelector(`#${CSS && CSS.escape ? CSS.escape(id) : id}`);
        } catch {
          return container.querySelector(`#${id}`);
        }
      },
      querySelector: (sel) => container.querySelector(sel),
      querySelectorAll: (sel) => container.querySelectorAll(sel),
      createElement: (tag) => document.createElement(tag),
      // body points to container (so scripts using document.body.* will work)
      body: container,
      // allow appendChild if code uses document.body.appendChild(...)
      appendChild: (el) => container.appendChild(el),
      // convenience: innerHTML
      get innerHTML() {
        return container.innerHTML;
      },
      set innerHTML(v) {
        container.innerHTML = v;
      },
      // simple event attachers (optional)
      addEventListener: (evt, fn, opts) => container.addEventListener(evt, fn, opts),
      removeEventListener: (evt, fn, opts) => container.removeEventListener(evt, fn, opts),
    };

    // --- Collect scripts from head and body ---
    // We want to treat external scripts (<script src="...">) differently (append real script tags)
    const inlineScriptContents = [];
    const externalScriptElements = [];

    // collect script tags from parsed doc (head & body)
    const parsedScripts = Array.from(doc.querySelectorAll("script"));
    parsedScripts.forEach((s) => {
      if (s.src) {
        // external: store src and attributes
        externalScriptElements.push(s);
      } else {
        inlineScriptContents.push(s.textContent || "");
      }
    });

    // --- Append external scripts into shadowRoot as real script elements ---
    // Append in order to preserve dependencies
    const appendExternalScripts = (list) => {
      return new Promise((resolve) => {
        if (!list.length) return resolve();
        let i = 0;
        const appendNext = () => {
          const s = list[i];
          const newScript = document.createElement("script");
          // copy attributes
          Array.from(s.attributes).forEach((attr) => newScript.setAttribute(attr.name, attr.value));
          // set src
          newScript.src = s.src;
          // ensure appended to shadowRoot so scoping is consistent
          shadowRoot.appendChild(newScript);
          // on load continue
          newScript.onload = () => {
            i += 1;
            if (i >= list.length) resolve();
            else appendNext();
          };
          newScript.onerror = (e) => {
            console.error("Failed to load external script inside Shadow DOM:", s.src, e);
            i += 1;
            if (i >= list.length) resolve();
            else appendNext();
          };
        };
        appendNext();
      });
    };

    // --- Execute inline scripts in order ---
    const runInlineScripts = async (scriptsArray) => {
      for (const scriptText of scriptsArray) {
        if (!scriptText || !scriptText.trim()) continue;
        try {
          // Wrap the script into a function that receives a `document` param that points to the shadow doc wrapper
          // Also provide shadowRoot, window, localStorage, fetch, console to the script scope.
          // Using Function constructor creates an isolated execution context (not the global scope).
          const fn = new Function(
            "document",
            "shadowRoot",
            "window",
            "localStorage",
            "fetch",
            "console",
            scriptText
          );
          // call with our wrappers so inside the script `document` refers to docWrapper
          fn.call(null, docWrapper, shadowRoot, window, window.localStorage, window.fetch, console);
        } catch (err) {
          // Log the error but continue executing next scripts
          console.error("Error executing inline script in HTML component:", err);
        }
      }
    };

    // Execute external scripts first (if they are intended to provide globals), then inline scripts
    (async () => {
      await appendExternalScripts(externalScriptElements);
      await runInlineScripts(inlineScriptContents);
    })();

    // cleanup function on re-render/unmount
    return () => {
      try {
        clearShadow();
      } catch (e) {
        // ignore cleanup errors
      }
    };
  }, [htmlContent]);

  return <div ref={hostRef} style={{ width: "100%", display: "block" }} />;
};

HTML.propTypes = {
  htmlContent: PropTypes.string,
};

export default HTML;
